import { Controller } from 'stimulus';

const ROLES_THAT_SKIP_REMAINING_STEPS = ['student', 'other_role'];

export default class extends Controller {
  static targets = ['step', 'submitButton', 'otherRoleRadio', 'otherRoleField', 'otherRoleInput'];
  static values = {
    currentStep: { type: Number, default: 0 },
  };

  connect() {
    this.applyVisibility();
    this.applyOtherRoleVisibility();
    this.updateSubmitButton();
  }

  selectOption(event) {
    const step = event.target.closest('[data-onboarding-poll-target="step"]');
    const stepIndex = parseInt(step.dataset.step, 10);
    const value = event.target.value;

    if (stepIndex === 0) {
      // Changing Q1 (role) always resets the downstream flow so users don't
      // carry stale Q2/Q3 answers from a previous role selection. Student and
      // Other skip Q2/Q3 entirely; standard roles drop back to Q2 only.
      this.clearStepsAfter(0);
      this.currentStepValue = ROLES_THAT_SKIP_REMAINING_STEPS.includes(value) ? 0 : 1;
      this.applyOtherRoleVisibility();
    } else if (stepIndex >= this.currentStepValue) {
      this.currentStepValue = stepIndex + 1;
    }

    this.applyVisibility();
    this.updateSubmitButton();
  }

  clearStepsAfter(stepIndex) {
    this.stepTargets.forEach((step) => {
      const index = parseInt(step.dataset.step, 10);
      if (index <= stepIndex) {
        return;
      }
      step.querySelectorAll('input[type="radio"]:checked').forEach((input) => {
        input.checked = false;
      });
    });
  }

  applyVisibility() {
    this.stepTargets.forEach((step) => {
      const index = parseInt(step.dataset.step, 10);
      const visible = index <= this.currentStepValue;
      const wasHidden = step.classList.contains('onboarding-poll__step--hidden');

      if (visible && wasHidden) {
        // Seed opacity:0 and maxHeight:0 before swapping display:none → block so the
        // transition has a real "from" value (elements at display:none don't track one).
        step.style.opacity = '0';
        step.style.maxHeight = '0';
        step.classList.remove('onboarding-poll__step--hidden');
        void step.offsetHeight;
        step.style.opacity = '';
        step.style.maxHeight = '';
      } else {
        step.classList.toggle('onboarding-poll__step--hidden', !visible);
      }

      step.inert = !visible;

      // Disable inputs in hidden steps so they aren't tabbable or submitted,
      // and clear any previously-selected values so the user can't submit a
      // stale answer for a step they no longer see (e.g., picking Designer
      // -> answering Q2 -> changing Q1 to Student).
      step.querySelectorAll('input[type="radio"]').forEach((input) => {
        input.disabled = !visible;
        if (!visible) {
          input.checked = false;
        }
      });
    });
  }

  // Show the free-text input only when the user picks "Other" for Q1. Hidden
  // and disabled otherwise so it isn't tabbable or submitted with a stale
  // value if the user changes their role selection.
  applyOtherRoleVisibility() {
    if (!this.hasOtherRoleFieldTarget || !this.hasOtherRoleInputTarget) {
      return;
    }

    const visible = this.hasOtherRoleRadioTarget && this.otherRoleRadioTarget.checked;

    this.otherRoleFieldTarget.classList.toggle('onboarding-poll__other-role--hidden', !visible);
    this.otherRoleInputTarget.disabled = !visible;

    if (!visible) {
      this.otherRoleInputTarget.value = '';
    }
  }

  updateSubmitButton() {
    if (!this.hasSubmitButtonTarget) {
      return;
    }

    this.submitButtonTarget.disabled = !this.allRequiredAnswered();
  }

  allRequiredAnswered() {
    const visibleSteps = this.stepTargets.filter((step) => {
      const index = parseInt(step.dataset.step, 10);
      return index <= this.currentStepValue;
    });

    if (visibleSteps.length === 0) {
      return false;
    }

    const radiosAnswered = visibleSteps.every((step) =>
      step.querySelector('input[type="radio"]:checked')
    );
    if (!radiosAnswered) {
      return false;
    }

    // When "Other" is selected for Q1 the free-text input becomes required;
    // otherwise the user could submit an empty other_role_value.
    if (this.hasOtherRoleRadioTarget && this.otherRoleRadioTarget.checked) {
      return this.hasOtherRoleInputTarget && this.otherRoleInputTarget.value.trim().length > 0;
    }

    return true;
  }
}
